import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Star, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'professionalism', label: 'Professionalism' },
  { key: 'friendliness', label: 'Friendliness' },
  { key: 'helpfulness', label: 'Helpfulness' },
  { key: 'communication', label: 'Communication' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'response_time', label: 'Response time' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Customer-facing feedback form rendered when a QR card is scanned.
 * The card_code in the URL acts as the access token — no auth required.
 * On submit, the backend snapshots the staff member currently assigned to
 * the card so historical attribution is preserved across reassignments.
 */
export default function PublicCardFeedbackPage() {
  const { cardCode } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [catRatings, setCatRatings] = useState({});
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/feedback/card-info/${cardCode}`);
        setInfo(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'This QR card is not active.');
      } finally {
        setLoading(false);
      }
    })();
  }, [cardCode]);

  const setCatRating = (key, val) => setCatRatings((p) => ({ ...p, [key]: val }));

  const submit = async () => {
    if (rating < 1) {
      toast.error('Please tap a star to rate.');
      return;
    }
    if (!comment.trim()) {
      toast.error('Please write a comment — it is required.');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/card/${cardCode}`, {
        rating,
        category_ratings: catRatings,
        comment: comment.trim(),
        contact_name: name.trim() || null,
        contact_email: email.trim(),
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-border rounded-sm">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
            <p className="text-lg font-semibold">
              {/not found/i.test(error) ? 'Card not found' : 'Card not active'}
            </p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-green-500/30 rounded-sm">
          <CardContent className="p-8 text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
            <p className="text-lg font-semibold">Thank you!</p>
            <p className="text-sm text-muted-foreground">
              Your feedback has been recorded. The team appreciates it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex items-start sm:items-center justify-center" data-testid="public-card-feedback">
      <Card className="max-w-md w-full bg-card border-border rounded-sm my-6">
        <CardContent className="p-6 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-primary">Customer feedback</p>
            <h1 className="text-2xl font-barlow tracking-wider mt-1">
              {info?.employee_name ? `Rate ${info.employee_name}` : 'Leave Feedback'}
            </h1>
            {info?.event_label && (
              <p className="text-xs text-muted-foreground mt-0.5">{info.event_label}</p>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">Card: {info?.card_code}</p>
          </div>

          {/* Rating */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your rating</Label>
            <div className="flex gap-2 mt-2" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className="focus:outline-none"
                  data-testid={`star-${n}`}
                >
                  <Star
                    className={`w-8 h-8 ${n <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Category star ratings */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rate each category</Label>
            <div className="space-y-2 mt-2">
              {CATEGORIES.map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-2" data-testid={`cat-row-${c.key}`}>
                  <span className="text-sm text-foreground">{c.label}</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setCatRating(c.key, n)}
                        className="focus:outline-none"
                        data-testid={`cat-${c.key}-star-${n}`}
                      >
                        <Star
                          className={`w-6 h-6 ${n <= (catRatings[c.key] || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Comments <span className="text-primary">*</span></Label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Tell us what we did well or could improve…"
              className="w-full mt-1 p-2 bg-background border border-border rounded-sm text-sm"
              data-testid="comment-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-background border-border rounded-sm" data-testid="contact-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email <span className="text-primary">*</span></Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-background border-border rounded-sm" data-testid="contact-email" />
            </div>
          </div>

          <Button
            onClick={submit}
            disabled={submitting || rating < 1}
            className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider font-semibold"
            data-testid="submit-feedback-btn"
          >
            {submitting ? 'Submitting…' : 'Submit Feedback'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
